<script lang="ts" setup>
import type Charger from '@/classes/charger_classes/charger'
import type Vehicle from '@/classes/vehicle_classes/vehicle'
import BookingDisplayListCard from '@/components/booking/BookingDisplayListCard.vue'
import GenericDialog from '@/components/dialog-wrappers/GenericDialog.vue'
import ElevatedBtn from '@/components/ui-elements/buttons/ElevatedBtn.vue'
import OutlinedBtn from '@/components/ui-elements/buttons/OutlinedBtn.vue'
import TripSelectedVehicleCard from '@/components/vehicles/TripSelectedVehicleCard.vue'
import VehicleBookingContent from '@/components/vehicles/VehicleBookingContent.vue'
import { GettersTypes, MainDialogContent, MutationTypes, type State } from '@/store/store_types'
import { useDashboardNavStore } from '@/store/stores/dashboard_nav.store'
import { sortBookingsByProximityToCurrentDate } from '@/utils/bookingsSortingUtils'
import getAssetSrc from '@/utils/getAssetSrc'
import { computed, ref } from 'vue'
import { useStore } from 'vuex'

const store = useStore<State>()
const fetching = computed<boolean>(() => store.getters[GettersTypes.fetching])
const upcomingBookings = computed(() => store.getters[GettersTypes.getUpcomingBookings])
const sortedUpcomingBookings = computed(() => {
  return sortBookingsByProximityToCurrentDate(upcomingBookings.value)
})

const savedChargers = computed<Charger[]>(() => store.getters[GettersTypes.getSavedChargers])
const defaultChargerImage = getAssetSrc('icons/DC.svg')
const selectedVehicle = computed<Vehicle | undefined>(
  () => store.getters[GettersTypes.selectedVehicleData],
)
const showBookingDialog = ref(false)

const dashboardNavStore = useDashboardNavStore()

function showLocationsManager() {
  dashboardNavStore.setDashboardDialogContent('locations-manager')
  dashboardNavStore.setShowDashboard(true)
}

function showAllBookings() {
  dashboardNavStore.setDashboardDialogContent('upcoming-bookings')
  dashboardNavStore.setShowDashboard(true)
}

function flyToAndOpenCharger(charger: Charger) {
  // trigger fly to point
  store.commit(MutationTypes.setFlyToPoint, {
    lat: charger.coordinates.latitude,
    lng: charger.coordinates.longitude,
  })
  // open charger details
  setTimeout(() => {
    store.commit(MutationTypes.setSelectedCharger, charger.id)
    store.commit(MutationTypes.setMainDialogContent, MainDialogContent.CHARGING_STATION_DETAILS)
  }, 2000)
}
</script>

<template>
  <v-card flat rounded="lg" color="pwt_snow" class="mb-5 mx-3 pa-6 pb-10" elevation="3">
    <!-- selected vehicle section -->
    <v-row no-gutters justify="space-between" align="center" class="mb-5">
      <span class="text-h6 text-tertiary"> Current default vehicle </span>
      <ElevatedBtn style="min-width: 100px" @click="showBookingDialog = true"> Book </ElevatedBtn>
    </v-row>
    <TripSelectedVehicleCard show-boot-capacity />
    <!-- selected vehicle section: booking dialog -->
    <GenericDialog
      v-if="selectedVehicle && selectedVehicle.directusId"
      :activator="showBookingDialog"
      @close="showBookingDialog = false"
    >
      <VehicleBookingContent :vehicle="selectedVehicle" />
    </GenericDialog>
    <!-- bookings section -->
    <v-row no-gutters justify="space-between" class="mt-10 mb-5">
      <span class="text-h6 text-tertiary"> Upcoming bookings </span>
      <OutlinedBtn style="min-width: 100px" @click="showAllBookings"> View all </OutlinedBtn>
    </v-row>
    <!-- bookings section: loading animation  -->
    <div v-if="fetching">
      <v-skeleton-loader
        type="sentences, avatar"
        v-for="i in 2"
        :key="i"
        class="mb-3 d-flex align-center flex-nowrap"
      />
    </div>
    <!-- bookings section: empty state -->
    <span v-else-if="sortedUpcomingBookings.length === 0" class="pa-1">
      You have no upcoming bookings.
    </span>
    <!-- bookings section: list items -->
    <div v-else>
      <BookingDisplayListCard
        v-for="booking in sortedUpcomingBookings.slice(0, 3)"
        :key="booking.local_id"
        :booking="booking"
      />
    </div>
    <!-- saved charging stations section -->
    <v-row no-gutters justify="space-between" class="mt-10 mb-5">
      <span class="text-h6 text-tertiary"> Saved charging stations </span>
      <OutlinedBtn style="min-width: 100px" @click="showLocationsManager"> View all </OutlinedBtn>
    </v-row>
    <!-- saved charging stations section: loading animation -->
    <div v-if="fetching">
      <v-skeleton-loader type="list-item-avatar-two-line" v-for="i in 2" :key="i" class="mb-3" />
    </div>
    <!-- saved charging stations section: empty state -->
    <span v-else-if="savedChargers.length === 0" class="pa-1">
      You have no saved charging stations.
    </span>
    <!-- saved charging stations section: list items -->
    <div v-else>
      <v-card
        v-for="charger in savedChargers.slice(0, 3)"
        :key="charger.id"
        class="d-flex align-center flex-nowrap justify-space-between pa-3 mb-3"
        flat
        color="pwt_alabaster"
        rounded="lg"
        @click="flyToAndOpenCharger(charger)"
      >
        <v-col md="2" class="pa-0 d-flex align-center">
          <img :src="charger.pinIconSrc" width="42px" height="42px" />
        </v-col>
        <v-col cols="10" class="pa-0">
          <v-card-title class="py-0 pl-0"> {{ charger.name }} </v-card-title>
          <v-card-subtitle class="pl-0">
            {{ charger.address }}
          </v-card-subtitle>
        </v-col>
      </v-card>
    </div>
  </v-card>
</template>
